import React from "react";
import { MbscCalendarEventData } from "@mobiscroll/react";
import { Grid2, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Cancel, DoNotDisturb, HourglassEmpty } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { BookingScheduleInterface } from "../../features/Booking-Form/typings/booking.types";
import { TeamMember } from "../../features/Booking-Form/typings/team-member";
import { ScheduleEventCheckbox } from "./schedule-event-checkbox.component";
import { ScheduleEventContentIcon } from "./schedule-event-content-icon.component";
import { hexToRGB } from "../FacilityManager/Functions/hexColorToRGB";
import { calBrightness } from "../FacilityManager/Functions/calculateBrightness";
import { BookingRequestsStatus } from "../BookingRequest/typings/booking-requests.types";
import { handleFirstAidUserName } from "../../features/Schedule/schedule.functions";
import { ScheduleMode } from "../../features/Schedule/partial/ScheduleModeToggle.partial";
import {
  resolveScheduleExternalMemberCalendarLabel,
  resolveScheduleVisitorCalendarLabel
} from "./functions/schedule-visitor-display-name.function";
import { resolveScheduleEventPlaceName } from "./functions/resolve-schedule-event-place-name.function";
import { bookingHasSeriesId } from "./functions/booking-has-series-id.function";
import { ScheduleSeriesBookingIndicator } from "./schedule-series-booking-indicator.component";
import { SCHEDULE_CALENDAR_EVENT_ICON_MIN_WIDTH_PX } from "./schedule-calendar-event-icon-min-width.constant";

type P = {
  event: MbscCalendarEventData;
  monthView?: boolean;
  schedule: BookingScheduleInterface;
  selectedColleagues?: TeamMember[];
  users?: TeamMember[];
  scheduleMode: ScheduleMode;
  selectedAppointments?: number[];
  setSelectedAppointment?: (selectedAppointment: number) => void;
};

export const ScheduleEventContent: React.FC<P> = ({
  event,
  monthView,
  schedule,
  selectedColleagues,
  users,
  scheduleMode,
  selectedAppointments,
  setSelectedAppointment
}) => {
  const { t } = useTranslation();
  const { userInformation } = useSelector((state: RootState) => state.login);

  const theme = useTheme();
  const hideEventIcons = useMediaQuery(
    theme.breakpoints.down(SCHEDULE_CALENDAR_EVENT_ICON_MIN_WIDTH_PX)
  );

  const isColleagueMode = scheduleMode === ScheduleMode.Colleague;
  const displayCheckbox =
    !hideEventIcons &&
    isColleagueMode &&
    DateTime.fromISO(schedule.startDate).valueOf() > DateTime.now().valueOf() &&
    (selectedColleagues
      ? selectedColleagues
          .map((colleague: { userId: string }) => colleague.userId)
          .includes(userInformation.sub) && schedule.userBookedFor === userInformation.sub
      : true);

  const bookedForUser = users?.find(user => user.userId === schedule.userBookedFor);
  const multipleUsersSelected = users && users.length >= 2;

  const bookedForUserName = (() => {
    const isFirstAid = handleFirstAidUserName(bookedForUser?.firstName);
    if (isFirstAid) return isFirstAid;
    else return `${bookedForUser?.firstName} ${bookedForUser?.surname}`.trim();
  })();

  const visitorCalendarLabel = resolveScheduleVisitorCalendarLabel(schedule);
  const showVisitorLabel = visitorCalendarLabel !== null;
  const externalMemberCalendarLabel = resolveScheduleExternalMemberCalendarLabel(schedule);
  const showExternalMemberLabel = externalMemberCalendarLabel !== null && !showVisitorLabel;
  const showUserName =
    multipleUsersSelected && bookedForUser && !showVisitorLabel && !showExternalMemberLabel;
  const placeName = resolveScheduleEventPlaceName(schedule, t);

  const calendarLabelText = (() => {
    if (showVisitorLabel) {
      return visitorCalendarLabel;
    }
    if (showExternalMemberLabel) {
      return externalMemberCalendarLabel;
    }
    if (showUserName) {
      return bookedForUserName;
    }
    const visitOnlySuffix = schedule.isVisitOnly ? ` · ${t("_schedule_visitOnlyBadge")}` : "";
    return `${placeName}${visitOnlySuffix}`;
  })();

  const showLocationInWeekView = showUserName || showVisitorLabel || showExternalMemberLabel;
  const eventChipColor = calBrightness(hexToRGB(event.original?.color));
  const seriesIndicator = (
    <ScheduleSeriesBookingIndicator seriesId={schedule.seriesId} color={eventChipColor} />
  );

  const hasSeries = bookingHasSeriesId(schedule.seriesId);
  const showTrailingControls = hasSeries || displayCheckbox;

  return (
    <Grid2 container data-testid="calendar-item" direction="column" sx={{ minWidth: 0 }}>
      <Grid2
        container
        wrap="nowrap"
        alignItems="center"
        data-testid="calendar-item-label-row"
        sx={{ width: "100%", minWidth: 0, flexWrap: "nowrap" }}
      >
        <Grid2
          size="grow"
          sx={{
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            position: "relative"
          }}
        >
          {!hideEventIcons && isColleagueMode && (
            <InitStatusIcon
              status={schedule.status}
              color={calBrightness(hexToRGB(event.original?.color))}
            />
          )}

          <Grid2 sx={{ display: "flex", flexShrink: 0, alignItems: "center", lineHeight: 0 }}>
            <ScheduleEventContentIcon
              schedule={schedule}
              userId={userInformation.sub}
              textColor={calBrightness(hexToRGB(event.original?.color))}
              showConferenceVisitorContactLink={isColleagueMode}
            />
          </Grid2>

          <Typography
            variant={"inherit"}
            component="span"
            noWrap
            sx={[
              {
                paddingLeft: 0.2,
                color: eventChipColor,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis"
              },
              schedule.status === BookingRequestsStatus.REJECTED
                ? { textDecoration: "line-through" }
                : { textDecoration: "" }
            ]}
          >
            {calendarLabelText}
          </Typography>
        </Grid2>

        {showTrailingControls && (
          <Grid2
            size="auto"
            data-testid="calendar-item-trailing"
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              ml: 0.25
            }}
          >
            {hasSeries && seriesIndicator}
            {displayCheckbox && (
              <ScheduleEventCheckbox
                schedule={schedule}
                selectedAppointments={selectedAppointments}
                setSelectedAppointment={setSelectedAppointment}
              />
            )}
          </Grid2>
        )}
      </Grid2>

      {!monthView && (
        <Grid2 container data-testid="calendar-item-week-view" sx={{ flexDirection: "column" }}>
          {showLocationInWeekView && (
            <Grid2>
              <Typography variant={"inherit"} sx={{ color: eventChipColor }}>
                {event.title}
                {schedule.isVisitOnly && !visitorCalendarLabel
                  ? ` · ${t("_schedule_visitOnlyBadge")}`
                  : ""}
              </Typography>
            </Grid2>
          )}

          <Grid2>
            <Typography variant={"inherit"} sx={{ color: eventChipColor }}>
              {event.start} - {event.end}
            </Typography>
          </Grid2>
        </Grid2>
      )}
    </Grid2>
  );
};

type InitStatusIconProps = {
  status: BookingRequestsStatus | undefined;
  color?: "black" | "white";
};
export function InitStatusIcon({ status, color }: Readonly<InitStatusIconProps>) {
  switch (status) {
    case BookingRequestsStatus.APPROVED:
      return "";
    case BookingRequestsStatus.CANCELLED:
      return <Cancel sx={{ width: 18, height: 18, color: color }} />;
    case BookingRequestsStatus.REJECTED:
      return <DoNotDisturb sx={{ width: 18, height: 18, color: color }} />;
    case BookingRequestsStatus.REQUESTED:
      return <HourglassEmpty sx={{ width: 18, height: 18, color: color }} />;
    default:
      return "";
  }
}
